import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip } from './ui/Tooltip';
import { RadiantStar } from './ui/RadiantStar';
export function AboutSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  return <section id="about" className="relative py-32 overflow-hidden" data-id="element-1" data-mp="2bd7fx8m6msc3">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-2" data-mp="27dymy5yyuyhl">
        <img src="https://images.unsplash.com/photo-1516653980844-c68233954853?q=80&w=2070&auto=format&fit=crop" alt="" className="w-full h-full object-cover object-bottom" data-id="element-3" data-mp="22lyu0xhbd5ui" />
        <div className="absolute inset-0 bg-gradient-to-r from-night-900 via-night-900/80 to-night-900/40" data-id="element-4" data-mp="1so14m6npx30b"></div>
        <div className="absolute inset-0 bg-grain opacity-20 mix-blend-overlay" data-id="element-5" data-mp="1nnd4mvhihbty"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-6" data-mp="1jnvydzu9ganq">
        <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-7" data-mp="ssck53ui5jic">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-8" data-mp="sqrmh5cw5kg9">
            <div className="flex items-center gap-4 mb-8" data-id="element-9" data-mp="1bi2exqy9m5a9">
              <h2 className="font-serif text-4xl md:text-5xl text-gold-400 text-glow" data-id="element-10" data-mp="1bghh9sgnm686">
                About Me: Grounded in Connection
              </h2>
              <div className="hidden sm:block" data-id="element-11" data-mp="12gufv6nxd0f4">
                <RadiantStar size={40} delay={0.3} data-id="element-12" data-mp="12k8jei8brfo5" />
              </div>
            </div>

            <div className="space-y-6 text-cream-50/90 text-lg leading-relaxed" data-id="element-13" data-mp="16lkc4wmhwh0k">
              {/* Teaser Text - Always Visible */}
              <p data-id="element-14" data-mp="3r9gjjs4rhek7">
                I started my career as an engineer, driven by curiosity,
                structure, and problem-solving. Over the years, my path evolved
                through roles in sales, business development, supply chain, and
                leadership.
              </p>
              <p data-id="element-15" data-mp="1o0wmhse4a8az">
                During those years, I learned that work and life are deeply
                connected. The moments where I felt strongest at work were often
                the moments where life outside of work was in balance too.
                Friends, family, nature, and my dog Django have always been an
                important foundation for me during both challenging and
                successful periods in life.
              </p>

              {/* Expandable Text */}
              <AnimatePresence data-id="element-16" data-mp="103h6qhrkuk5b">
                {isExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="overflow-hidden space-y-6" data-id="element-17" data-mp="105bue3u8gqd3">
                    <p data-id="element-18" data-mp="3gri41w5ubqtv">
                      As my responsibilities grew, so did the number of choices,
                      expectations, and pressure that came with them. Coaching
                      helped me create more confidence in the decisions I had to
                      make as a leader, while also helping me discover more
                      purpose and direction in both work and life.
                    </p>
                    <p data-id="element-19" data-mp="8ado1tp3jtri">
                      I believe answers can come from many places. Silent
                      retreats, breathing exercises, meaningful conversations,
                      or simply being in nature. But the most powerful insights
                      I found came through coaching. Not because someone gave me
                      the answers, but because coaching helped me slow down
                      enough to truly hear my own.
                    </p>
                    <p data-id="element-20" data-mp="1q7qt2ge4yknb">
                      That experience made me fall in love with coaching and
                      helping others find that same clarity and confidence for
                      themselves.
                    </p>
                    <p data-id="element-21" data-mp="204vf8g7zeg44">
                      Today, alongside my leadership role, I am building Mark V
                      Coaching and further developing my coaching expertise
                      through the International Coaching Federation (ICF). Using
                      my own 4-step coaching model, I help people step back from
                      the pressure of daily life, regain ownership of their
                      choices, and create a direction that truly feels like
                      their own.
                    </p>
                  </motion.div>}
              </AnimatePresence>

              {/* Read More / Read Less Toggle */}
              <button onClick={() => setIsExpanded(!isExpanded)} className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-medium transition-colors focus:outline-none group" data-id="element-22" data-mp="2skjtghu6yjh0">
                <span data-id="element-23" data-mp="2jpdht288p76k">{isExpanded ? 'Read less' : 'Read my full story'}</span>
                {isExpanded ? <ChevronUp size={20} className="group-hover:-translate-y-1 transition-transform" data-id="element-24" data-mp="2ojzrn0yoaroj" /> : <ChevronDown size={20} className="group-hover:translate-y-1 transition-transform" data-id="element-25" data-mp="2eq98mblbf9g8" />}
              </button>

              {/* Closing quote-like statement */}
              <div className="pt-4 border-l-2 border-gold-400/50 pl-5" data-id="element-26" data-mp="bbqhh7ffaufj">
                <p className="font-serif italic text-xl md:text-2xl text-cream-50 leading-relaxed" data-id="element-27" data-mp="6kqmw8ts0og7">
                  The safest route down the mountain is often the one everyone
                  else already took. But the most meaningful paths are usually
                  the ones you create yourself.
                </p>
              </div>
            </div>

            {/* ICF Badge - Always Visible */}
            <div className="mt-8 space-y-3" data-id="element-28" data-mp="pbhtx3qak2rf">
              <div className="flex items-center space-x-3 text-gold-400/80 bg-night-900/50 backdrop-blur-sm p-4 rounded-lg inline-flex border border-gold-400/20" data-id="element-29" data-mp="pbrh6jjybvtf">
                <CheckCircle2 size={20} data-id="element-30" data-mp="gbbh7k2f36tb" />
                <span className="font-medium tracking-wide text-sm" data-id="element-31" data-mp="gaa5rehhoib2">
                  ICF Member (ACC Credential Pending)
                </span>
              </div>
              <p className="text-cream-50/70 text-sm leading-relaxed max-w-lg" data-id="element-32" data-mp="310iymx4q28ci">
                I believe in and apply the{' '}
                <a href="https://coachingfederation.org/resource/icf-core-competencies/" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-300 underline underline-offset-2 transition-colors" data-id="element-33" data-mp="1gkb4pmck27eh">
                  ICF Core Competencies
                </a>{' '}
                in my coaching practice.
              </p>
            </div>
          </motion.div>

          {/* Right side - Mark's portrait */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden md:flex items-start justify-center" data-id="element-34" data-mp="24a5kocs68c2y">
            <div className="relative" data-id="element-35" data-mp="2krgog2pwo56b">
              <div className="absolute -inset-3 bg-gold-400/10 rounded-2xl transform rotate-2" data-id="element-36" data-mp="2kqfc7ux5lcn0"></div>
              <img src="https://cdn.magicpatterns.com/uploads/sjqk9147GGzZcR6Q2opMuU/ChatGPT_Image_26_mei_2026,_20_30_35.png" alt="Mark Vermeulen" className="relative rounded-2xl shadow-2xl w-full max-w-md object-contain border border-gold-400/20" data-id="element-37" data-mp="2gou5krb7pul9" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}